import { useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DateTime } from "luxon";
import { z } from "zod";
import { AuthContext } from "../../context/context";
import {
  updateMilestoneStatus,
  deleteMilestone,
  updateMilestone,
} from "../../api";
import { motion } from "framer-motion";
import { TbEdit, TbTrash, TbEye } from "react-icons/tb";
import Card from "./Card";
import Popup from "../Elements/Popup";
import MilestoneCheckCard from "./CardMilestoneCheck";
import InputText from "../Inputs/InputText";
import InputDate from "../Inputs/InputDate";
import BtnSecondaryS from "../Buttons/BtnSecondaryS";
import animation from "../../Animations/milestones-animation.json";
import { useLanguage } from "../../context/LanguageContext";
import Toast from "../Elements/Toast";
import { useNotifications } from "../../context/NotificationContext";
import PropTypes from "prop-types";

const editMilestoneSchema = z.object({
  title: z.string().min(1, "Title is required"),
  deadline: z.string().min(1, "Deadline is required"),
});

const CardMilestones = ({ className, activeDay, showViewInCalendarButton = false, isInTab = false, customTitle, customDescription, cardClassName, useMaxHeight }) => {
  const { userData, setUserData } = useContext(AuthContext);
  const { copy } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const { loadNotifications } = useNotifications();

  const [milestoneToDelete, setMilestoneToDelete] = useState(null);
  const [milestoneToEdit, setMilestoneToEdit] = useState(null);
  const [popupType, setPopupType] = useState(null);
  const [editFormData, setEditFormData] = useState({
    title: "",
    deadline: "",
    status: false,
  });
  const [editFormErrors, setEditFormErrors] = useState({});
  const [toast, setToast] = useState({ message: "", type: "success" });

  const hideDropdown = ["/calendar"].includes(location.pathname);

  // Debug logging for milestones
  console.log('🎯 CardMilestones - userData milestones:', {
    milestonesCount: userData?.milestones?.length || 0,
    milestones: userData?.milestones,
    hasUserData: !!userData
  });

  const openPopup = (type) => {
    // console.log('openPopup called with type:', type);
    setPopupType(type);
    // console.log('popupType set to:', type);
  };
  const closePopup = () => {
    setPopupType(null);
    setEditFormErrors({});
  };

  const activeDayTasks = ((userData.milestones || []) || []).filter(
    (milestone) =>
      milestone.deadline &&
      DateTime.fromISO(milestone.deadline).isValid &&
      activeDay.isValid &&
      DateTime.fromISO(milestone.deadline)
        .startOf("day")
        .equals(activeDay.startOf("day"))
  );

  const totalMilestones = activeDayTasks.length;
  const completedMilestones = activeDayTasks.filter(
    (task) => task.status
  ).length;

  const toggleMilestoneStatus = async (milestoneId) => {
    const milestone = (userData.milestones || []).find(
      (m) => m.id === Number(milestoneId)
    );
    if (!milestone) {
      setToast({
        message: copy.card_milestones.errors.status_update_failed,
        type: "error",
      });
      return;
    }
    const updatedStatus = !milestone.status;

    // Optimistically update UI
    const newUserData = { ...userData };
    const i = newUserData.milestones.findIndex(
      (m) => m.id === Number(milestoneId)
    );
    newUserData.milestones[i].status = updatedStatus;
    newUserData.milestones[i].statusChangedAt = new Date().toISOString();
    setUserData(newUserData);

    try {
      // Await API call
      await updateMilestoneStatus(Number(milestoneId), updatedStatus);
      loadNotifications();

      // Show success toast only after API confirms
      setToast({
        message: updatedStatus
          ? copy.card_milestones.errors.status_update_success
          : copy.card_milestones.errors.status_update_incomplete,
        type: "success",
      });
    } catch (error) {
      // Revert UI if API fails
      newUserData.milestones[i].status = !updatedStatus;
      setUserData(newUserData);
      let errorMessage = copy.card_milestones.errors.status_update_failed;
      if (error instanceof Error) {
        errorMessage += `: ${error.message}`;
      } else if (typeof error === "string") {
        errorMessage += `: ${error}`;
      } else if (error && error.response) {
        errorMessage += `: ${JSON.stringify(error.response)}`;
      }
      setToast({
        message: errorMessage,
        type: "error",
      });
    }
  };

  const milestoneDropdown = (milestoneId) => {
    // console.log('milestoneDropdown called with milestoneId:', milestoneId, typeof milestoneId);
    
    const options = [
      {
        text: copy.dropdown.edit,
        onClick: () => {
          // console.log('Edit option clicked for milestoneId:', milestoneId);
          handleEditClickMilestone(milestoneId);
        },
        icon: <TbEdit />,
      },
      {
        text: copy.dropdown.delete,
        onClick: () => {
          // console.log('Delete option clicked for milestoneId:', milestoneId);
          handleDeleteClickMilestone(milestoneId);
        },
        icon: <TbTrash />,
      },
    ];
    
    // console.log('Created dropdown options:', options);
    return options;
  };

  const handleEditClickMilestone = (milestoneId) => {
    // console.log('handleEditClickMilestone called with:', milestoneId, typeof milestoneId);
    
    // Convert string ID to number for milestone lookup
    const numericId = Number(milestoneId);
    const milestone = (userData.milestones || []).find((m) => m.id === numericId);
    // console.log('Found milestone:', milestone);
    
    if (!milestone) {
      // console.error('Milestone not found for ID:', numericId);
      setToast({
        message: copy.card_milestones.errors.no_milestone_edit,
        type: "error",
      });
      return;
    }
    
    setMilestoneToEdit(numericId);

    const formattedDeadline = DateTime.fromISO(milestone.deadline).toFormat(
      "yyyy-LL-dd"
    );
    setEditFormData({
      title: milestone.title,
      deadline: formattedDeadline,
      status: milestone.status,
    });

    // console.log('Opening popup for milestone edit');
    openPopup("milestone-edit");
  };

  const handleDeleteClickMilestone = (milestoneId) => {
    // Convert string ID to number for consistency
    const numericId = Number(milestoneId);
    setMilestoneToDelete(numericId);
    openPopup("milestone-delete");
  };

  const handleDeleteMilestone = async () => {
    if (!milestoneToDelete) {
      setToast({
        message: copy.card_milestones.errors.no_milestone_selected,
        type: "error",
      });
      return;
    }

    try {
      await deleteMilestone(milestoneToDelete);
      setUserData((prev) => ({
        ...prev,
        milestones: prev.milestones.filter(
          (m) => m.id != Number(milestoneToDelete)
        ),
      }));

      setToast({
        message: copy.card_milestones.errors.delete_success,
        type: "success",
      });
      setMilestoneToDelete(null);
      closePopup();
    } catch (err) {
      console.error("Error deleting milestone:", err.message);
      setToast({
        message: copy.card_milestones.errors.delete_failed,
        type: "error",
      });
    }
  };

  const handleEditMilestone = async () => {
    if (!milestoneToEdit) {
      setToast({
        message: copy.card_milestones.errors.no_milestone_edit,
        type: "error",
      });
      return;
    }

    const validation = editMilestoneSchema.safeParse(editFormData);

    if (!validation.success) {
      const errors = {};
      validation.error.errors.forEach((err) => {
        errors[err.path[0]] = err.message;
      });
      setEditFormErrors(errors);
      return;
    }

    try {
      const updatedData = {
        title: editFormData.title,
        deadline: editFormData.deadline,
      };

      const updatedMilestone = await updateMilestone(
        milestoneToEdit,
        updatedData
      );

      if (!updatedMilestone) {
        setToast({
          message: copy.card_milestones.errors.update_failed,
          type: "error",
        });
        return;
      }

      setUserData((prev) => ({
        ...prev,
        milestones: prev.milestones.map((m) =>
          m.id === milestoneToEdit ? { ...m, ...updatedMilestone } : m
        ),
      }));

      setToast({
        message: copy.card_milestones.errors.update_success,
        type: "success",
      });
      setMilestoneToEdit(null);
      closePopup();
    } catch (err) {
      console.error("Error editing milestone:", err.message);
      setToast({
        message: err.message || copy.card_milestones.errors.update_failed,
        type: "error",
      });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const calendarDropdown = [
    {
      text: copy.dropdown.view_all,
      link: "/calendar",
      icon: <TbEye />,
    },
  ];

  const currentYear = DateTime.local().year; // Get the current year
  const isPastYear = activeDay.year < currentYear;
  const isFutureYear = activeDay.year > currentYear;

  const noTasksMessage = isPastYear
    ? copy.card_milestones.messages.past_year
    : isFutureYear
      ? copy.card_milestones.messages.future_year
      : copy.card_milestones.messages.no_tasks.replace(
          "{date}",
          activeDay.toLocaleString(DateTime.DATE_MED)
        );

  const handleViewInCalendar = () => {
    navigate("/calendar");
  };

  const cardTitle = customTitle || (isInTab 
    ? copy.card_milestone.headline 
    : "Milestones");
  
  const cardDescription = customDescription || (isInTab 
    ? copy.card_milestone.description 
    : "Daily tasks, that bring you closer to your goals");

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      {activeDay && activeDayTasks.length > 0 ? (
        <Card
          title={cardTitle}
          description={cardDescription}
          options={!hideDropdown ? calendarDropdown : undefined}
          className={`${className || ""}`}
          useMaxHeight={useMaxHeight !== undefined ? useMaxHeight : !hideDropdown}
          cardClassName={cardClassName}
        >
          <div className="flex flex-col h-full">
            <div className="flex justify-between text-md mb-1!">
              <span>
                {activeDay.hasSame(DateTime.local(), 'day') ? 'Today: ' : 'Date: '}
                {new Date(activeDay).toLocaleDateString()}
              </span>
              <p>
                {completedMilestones} / {totalMilestones}
              </p>
            </div>

            <div className="flex-1 overflow-auto flex flex-col gap-2">
              {[...activeDayTasks]
            .sort((a, b) => a.status - b.status)
            .map((milestone) => (
              <motion.div
                key={milestone.id}
                layout
                transition={{ duration: 0.2 }}
              >
                <MilestoneCheckCard
                  id={milestone.id.toString()}
                  title={milestone.title}
                  status={milestone.status}
                  toggleMilestoneStatus={toggleMilestoneStatus}
                  milestoneDropdown={milestoneDropdown}
                />
              </motion.div>
            ))}
            </div>

            {showViewInCalendarButton && (
              <div className="pt-4 pb-4 flex justify-center">
                <BtnSecondaryS
                  buttonCTA="View all in calendar"
                  onClickLink={handleViewInCalendar}
                  type="button"
                />
              </div>
            )}
          </div>
        </Card>
      ) : (
        <Card
          title={cardTitle}
          description={cardDescription}
          options={!hideDropdown ? calendarDropdown : undefined}
          lottieAnimation={animation}
          details={noTasksMessage}
          className="justify-center"
          cardClassName={cardClassName || "h-full"}
        />
      )}

      <Popup
        isOpen={popupType === "milestone-delete"}
        onClose={closePopup}
        title={copy.card_milestone.popup.delete_title}
        description={copy.card_milestone.popup.delete_description}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.delete}
        onClickPrimaryLink={handleDeleteMilestone}
      />

      <Popup
        isOpen={popupType === "milestone-edit"}
        onClose={closePopup}
        title={copy.card_milestone.popup.edit_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={handleEditMilestone}
      >
        <div className="flex flex-col gap-4">
          <InputText
            label={copy.card_milestone.form.label_title}
            placeholder={copy.card_milestone.form.placeholder_title}
            name="title"
            value={editFormData.title}
            onChange={handleInputChange}
            error={editFormErrors.title}
            details={`${editFormData.title.length}/100 characters`}
          />
          <InputDate
            label={copy.card_milestone.form.label_deadline}
            name="deadline"
            value={editFormData.deadline}
            onChange={handleInputChange}
            error={editFormErrors.deadline}
          />
        </div>
      </Popup>
    </>
  );
};

CardMilestones.propTypes = {
  className: PropTypes.string,
  activeDay: PropTypes.instanceOf(DateTime),
  showViewInCalendarButton: PropTypes.bool,
  isInTab: PropTypes.bool,
};

export default CardMilestones;
